import type { TFunction } from "i18next";
import type { z } from "zod";
import type { LegalFieldPolicy } from "@/services/profileService";
import {
  createCompanyBusinessSchema,
  createCompanyLegalRepSchema,
  createIdentityDocumentSchema,
  createPersonPersonalSchema,
  contactSchema,
  paymentSchema,
  registeredAddressSchema,
} from "./validation";
import {
  isCompanyBusinessDirty,
  isCompanyLegalRepDirty,
  isContactDirty,
  isDocumentDirty,
  isPaymentDirty,
  isPersonPersonalDirty,
  isRegisteredAddressDirty,
} from "./profileDetailForm";
import {
  type DirtyFieldMap,
  type ProfileFormField,
  type ProfileFormValues,
  COMPANY_BUSINESS_FIELDS,
  COMPANY_LEGAL_REP_FIELDS,
  CONTACT_FIELDS,
  DOCUMENT_FIELDS,
  PAYMENT_FIELDS,
  PERSON_PERSONAL_FIELDS,
  REGISTERED_ADDRESS_FIELDS,
  isFieldExcluded,
} from "./profileFormTypes";

export type ProfileSectionId =
  | "person-personal"
  | "person-residence"
  | "person-identity"
  | "company-business"
  | "company-registered"
  | "company-legal-rep"
  | "contact"
  | "payment";

const SECTION_FIELDS: Record<ProfileSectionId, readonly ProfileFormField[]> = {
  "person-personal": PERSON_PERSONAL_FIELDS,
  "person-residence": REGISTERED_ADDRESS_FIELDS,
  "person-identity": DOCUMENT_FIELDS,
  "company-business": COMPANY_BUSINESS_FIELDS,
  "company-registered": REGISTERED_ADDRESS_FIELDS,
  "company-legal-rep": COMPANY_LEGAL_REP_FIELDS,
  contact: CONTACT_FIELDS,
  payment: PAYMENT_FIELDS,
};

export function getProfileSectionFields(
  sectionId: ProfileSectionId,
  fieldPolicy: LegalFieldPolicy,
): readonly ProfileFormField[] {
  if (sectionId !== "contact") {
    return SECTION_FIELDS[sectionId];
  }
  return SECTION_FIELDS.contact.filter((field) => !isFieldExcluded(fieldPolicy, field));
}

export function isProfileSectionDirty(
  sectionId: ProfileSectionId,
  dirtyFields: DirtyFieldMap,
): boolean {
  switch (sectionId) {
    case "person-personal":
      return isPersonPersonalDirty(dirtyFields);
    case "person-residence":
    case "company-registered":
      return isRegisteredAddressDirty(dirtyFields);
    case "person-identity":
      return isDocumentDirty(dirtyFields);
    case "company-business":
      return isCompanyBusinessDirty(dirtyFields);
    case "company-legal-rep":
      return isCompanyLegalRepDirty(dirtyFields);
    case "contact":
      return isContactDirty(dirtyFields);
    case "payment":
      return isPaymentDirty(dirtyFields);
    default:
      return false;
  }
}

export function pickSectionFormValues(
  values: ProfileFormValues,
  sectionId: ProfileSectionId,
  fieldPolicy: LegalFieldPolicy,
): Partial<ProfileFormValues> {
  const picked: Partial<ProfileFormValues> = {};
  for (const field of getProfileSectionFields(sectionId, fieldPolicy)) {
    picked[field] = values[field];
  }
  return picked;
}

export function createProfileSectionSchema(
  sectionId: ProfileSectionId,
  t: TFunction<"validation">,
  options: { fieldPolicy: LegalFieldPolicy },
): z.ZodType<Record<string, unknown>> {
  switch (sectionId) {
    case "person-personal":
      return createPersonPersonalSchema(t);
    case "person-residence":
    case "company-registered":
      return registeredAddressSchema(t);
    case "person-identity":
      return createIdentityDocumentSchema(t);
    case "company-business":
      return createCompanyBusinessSchema(t);
    case "company-legal-rep":
      return createCompanyLegalRepSchema(t);
    case "contact":
      return contactSchema(t, options.fieldPolicy);
    case "payment":
      return paymentSchema(t, options.fieldPolicy);
  }
}
